import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { IconCircleFilled } from '@tabler/icons-react';
import classnames from 'classnames';
import { FeatureCollection, Geometry, GeoJsonProperties } from 'geojson';
import { cellToBoundary, gridDisk, latLngToCell } from 'h3-js';
import { useAtom } from 'jotai';
import mapboxgl from 'mapbox-gl';
import Map, { Layer, Marker, NavigationControl, Source } from 'react-map-gl/mapbox';

import {
  popupBottomPositionFilter,
  popupLeftPositionFilter,
} from 'pages/Analytics/MarketOpportunity/HexMap';
import { createGeoJSONCircleMiles } from 'pages/Settings/GeoConfigSettings/GeoMap';
import {
  DealerData,
  Hex,
  HexData,
  LocalMarketData,
} from 'pages/Settings/LocalMarketSettings/api/useLocalMarket/useLocalMarket';
import { activeDealerTypeAtom } from 'pages/Settings/LocalMarketSettings/DealerSidebar';
import { CircleIconWithBorder } from 'pages/Settings/LocalMarketSettings/icons/CircleIcon';
import { IconPlus } from 'pages/Settings/LocalMarketSettings/icons/IconPlus';
import { StarIconWithBorder } from 'pages/Settings/LocalMarketSettings/icons/StarIcon';
import MapPopup from 'pages/Settings/LocalMarketSettings/MarketMap/MapPopup';
import SummaryPanel from 'pages/Settings/LocalMarketSettings/MarketMap/SummaryPanel';
import Flex from '@lotlinx-aws/sparky-component-library/Sparky/Flex';
import IconButton from '@lotlinx-aws/sparky-component-library/Sparky/IconButton';
import { COLOR } from '@lotlinx-aws/sparky-component-library/Sparky/styles/vars';
import { useToast } from '@lotlinx-aws/sparky-component-library/Sparky/Toast';

import { RESOLUTION, newSelectedHexsAtom, usedSelectedHexsAtom } from './MarketMap.atoms';
import styles from './MarketMap.module.scss';
import SelectorModal from './SelectorModal';
import SliderPopover from './SliderPopover/SliderPopover';

interface HexArrayData extends HexData {
  hex: string;
  color?: string;
}

export interface MarketMapProps {
  data?: LocalMarketData;
  reRenderKey: number;
  setReRenderKey: (key: number) => void;
  protectedHexs: string[];
  newKeyDealers: number[];
  usedKeyDealers: number[];
  highlightDealer: number | undefined;
  dealerList: DealerData[];
  clearDealers(dealerIdArray: number[] | undefined): void;
}

const mapColor = '#0d67f8';

/**
 * MarketMap
 */
export default function MarketMap({
  data,
  reRenderKey,
  setReRenderKey,
  dealerList,
  protectedHexs,
  newKeyDealers,
  usedKeyDealers,
  highlightDealer,
  clearDealers,
}: MarketMapProps) {
  const [activeDealerType] = useAtom(activeDealerTypeAtom);
  const [isSliderOpen, setIsSliderOpen] = useState<boolean>(false);
  const [previousSliderValue, setPreviousSliderValue] = useState<number[]>([0]);
  const [newSliderValue, setNewSliderValue] = useState<number[]>([15]);
  const [usedSliderValue, setUsedSliderValue] = useState<number[]>([15]);
  const [usedSelectedHexs, setUsedSelectedHexs] = useAtom(usedSelectedHexsAtom);
  const [newSelectedHexs, setNewSelectedHexs] = useAtom(newSelectedHexsAtom);
  const [deselectableDealers, setDeselectableDealers] = useState<any[]>([]);
  const [cursorStyle, setCursorStyle] = useState('default');

  const ref = useRef<HTMLHeadingElement>(null);
  const toastShowingRef = useRef<boolean>(false);
  const [mapDimensions, setMapDimensions] = useState<
    { height: number | undefined; width: number | undefined } | undefined
  >(undefined);

  const [popupData, setPopupData] = useState<HexData | undefined>(undefined);
  const [hoveredHex, setHoveredHex] = useState<string | undefined>(undefined);
  const [protectedCellClicked, setProtectedCellClicked] = useState<string | undefined>(undefined);
  const [nonSelectableCellClicked, setNonSelectableCellClicked] = useState<string | undefined>(
    undefined,
  );
  const [mapZoom, setMapZoom] = useState<number>(7);
  const [isSliderModal, setIsSliderModal] = useState<boolean>(false);
  useEffect(() => {
    setMapDimensions({ height: ref.current?.offsetHeight, width: ref.current?.offsetWidth });
  }, [ref.current]);
  const { addToast } = useToast();

  const [popupLocation, setPopupLocation] = useState<
    | {
        lat: number;
        lng: number;
        x: number;
        y: number;
      }
    | undefined
  >(undefined);

  const isNewDealer = activeDealerType === 'new';
  const sliderValue = isNewDealer ? newSliderValue : usedSliderValue;
  const setSliderValue = isNewDealer ? setNewSliderValue : setUsedSliderValue;
  const keyDealers = isNewDealer ? newKeyDealers : usedKeyDealers;
  const selectedHexs = isNewDealer ? newSelectedHexs : usedSelectedHexs;
  const setSelectedHexs = isNewDealer ? setNewSelectedHexs : setUsedSelectedHexs;

  const selectedHexsSet = useMemo(() => new Set(selectedHexs), [selectedHexs]);
  const protectedHexsSet = useMemo(() => new Set(protectedHexs), [protectedHexs]);

  const dealerListWithHexs = useMemo(() => {
    return (
      data?.dealerList.map((dealer) => {
        const hexCode = latLngToCell(dealer.location.lat, dealer.location.lng, RESOLUTION);
        return { ...dealer, hexCode };
      }) ?? []
    );
  }, [data?.dealerList, RESOLUTION]);

  useEffect(() => {
    setReRenderKey(Date.now());
  }, [data]);

  // * BUILDING MAP HEX DATA
  //* Add additional properties to hexData
  let dataHexs: Hex = useMemo(() => {
    return addHexProperties(data?.hexData);
  }, [data?.hexData]);

  //* Create list of selectable hexs
  const selectableSet = useMemo(() => {
    return buildSelectableList(selectedHexsSet, dataHexs);
  }, [selectedHexsSet, dataHexs]);

  //* Create Data for each Layer of Hexs
  const chunkedBaseHexArray = useMemo(() => {
    const fullData = Object.keys(dataHexs)
      .map((hex) => {
        const opacity = dataHexs[hex].opacity ?? 0.1;
        return {
          hex,
          ...dataHexs[hex],
          borderColor: '#fff',
          borderOpacity: 0.2,
          opacity: 0.1 + 0.8 * opacity,
          color: '#555',
        };
      })
      .filter((hex) => !selectedHexsSet.has(hex.hex));

    const chunkedData: HexArrayData[][] = [];

    while (fullData.length) {
      const chunk = fullData.splice(0, 100);
      chunkedData.push(chunk);
    }

    return chunkedData;
  }, [selectedHexs]);

  const highlightedDealerCell = useMemo(() => {
    const highlightedCell: HexArrayData[] = [];
    const dealerData = dealerList?.find((dealer) => dealer.dealerId === highlightDealer);
    const dealerHexCode = latLngToCell(
      dealerData?.location.lat ?? 0,
      dealerData?.location.lng ?? 0,
      RESOLUTION,
    );

    const dealerHex = data?.hexData[dealerHexCode];
    if (dealerHex) {
      highlightedCell.push({
        ...dealerHex,
        hex: dealerHexCode,
        borderColor: '#F41424',
        borderOpacity: 1,
        color: mapColor,
      });
    }

    return returnGeoJSON(highlightedCell);
  }, [highlightDealer, data?.hexData]);

  const protectedHexArray = useMemo(() => {
    let portectedHexsData: HexArrayData[] = [];
    Object.keys(dataHexs)?.forEach((hex, i) => {
      if (protectedHexsSet.has(hex) && data?.hexData[hex]) {
        let opacity = dataHexs[hex].opacity ?? 0.1;
        portectedHexsData.push({
          ...data?.hexData[hex],
          hex,
          borderColor: '#333',
          borderOpacity: 1,
          opacity: 0.2 + 0.8 * opacity,
          color: mapColor,
        });
      }
    });
    return returnGeoJSON(portectedHexsData);
  }, [dataHexs]);

  const selectedHexArray = useMemo(() => {
    let selectedHexsData: HexArrayData[] = [];
    Object.keys(dataHexs)?.forEach((hex, i) => {
      if (data?.hexData[hex] && selectedHexsSet.has(hex) && !protectedHexsSet.has(hex)) {
        const opacity = dataHexs[hex].opacity ?? 0.1;
        selectedHexsData.push({
          ...data?.hexData[hex],
          hex,
          borderColor: '#333',
          borderOpacity: 1,
          opacity: 0.2 + 0.8 * opacity,
          color: mapColor,
        });
      }
    });
    return returnGeoJSON(selectedHexsData);
  }, [selectedHexs]);

  // * ON CLICK HANDLER
  const onClickMapHandler = (event: mapboxgl.MapLayerMouseEvent) => {
    const {
      lngLat: { lat, lng },
    } = event;

    const hexCode = latLngToCell(lat, lng, RESOLUTION);

    if (selectableSet.has(hexCode)) {
      setPreviousSliderValue([0]);
      setSelectedHexs([...selectedHexs, hexCode]);

      // Show toast when cell is selected (only if not already showing)
      if (!toastShowingRef.current) {
        toastShowingRef.current = true;
        addToast({
          status: 'info',
          duration: 3000,
          description: 'Remember to save your changes',
        });

        // Reset the flag after the toast duration
        setTimeout(() => {
          toastShowingRef.current = false;
        }, 3000);
      }
    }

    const isSafeToDeselect = !protectedHexsSet.has(hexCode);
    if (selectedHexsSet.has(hexCode) && isSafeToDeselect) {
      // Check if the cell contains any key dealers
      const dealersInHex = dealerListWithHexs?.filter((dealer) => dealer.hexCode === hexCode);
      const keyDealersInHex = dealersInHex?.filter((dealer) =>
        keyDealers.includes(dealer.dealerId),
      );

      if (keyDealersInHex && keyDealersInHex.length > 0) {
        // Show SelectorModal when deselecting a cell with key dealers
        setDeselectableDealers(keyDealersInHex);
        setIsSliderModal(false);
      } else {
        // Directly deselect if no key dealers in the cell
        setSelectedHexs(selectedHexs.filter((hex) => hex !== hexCode));
      }
    }

    // Show warning toast when clicking on a protected cell
    if (protectedHexsSet.has(hexCode)) {
      addToast({
        status: 'error',
        duration: 4000,
        description: "You can't deselect cells in your default market radius",
      });

      // Set the protected cell as clicked and clear after 2 seconds
      setProtectedCellClicked(hexCode);
      setTimeout(() => {
        setProtectedCellClicked(undefined);
      }, 2000);
    }

    // Show error toast when clicking on non-selectable, non-selected, non-protected cells
    if (
      !selectableSet.has(hexCode) &&
      !selectedHexsSet.has(hexCode) &&
      !protectedHexsSet.has(hexCode)
    ) {
      addToast({
        status: 'error',
        duration: 3000,
        description:
          'Only adjacent cells can be selected. Choose a cell next to your current selection.',
      });

      // Set the non-selectable cell as clicked and clear after 2 seconds
      setNonSelectableCellClicked(hexCode);
      setTimeout(() => {
        setNonSelectableCellClicked(undefined);
      }, 2000);
    }
  };

  //* IF OVER A HEX SET SHOW THE POPUP WITH THAT HEXS DATA
  useEffect(() => {
    if (popupLocation) {
      const hexCode = latLngToCell(popupLocation.lat, popupLocation.lng, RESOLUTION);
      const activeHex = data?.hexData[hexCode];
      const dealersInHex = dealerListWithHexs?.filter((dealer) => dealer.hexCode === hexCode);
      const isSelectable = selectableSet.has(hexCode);
      const isProtected = protectedHexsSet.has(hexCode);

      const returnHexStatus = () => {
        if (isProtected) {
          return '';
        } else if (isSelectable) {
          return 'Click to select this cell';
        } else if (selectedHexsSet.has(hexCode)) {
          return 'Click to deselect this cell';
        }
        return '';
      };

      // Collect dealer data for active hex

      if (activeHex && popupLocation.x !== 0 && popupLocation.y !== 0) {
        setPopupData({ ...activeHex, dealersInHex: dealersInHex, hexStatus: returnHexStatus() });
      } else {
        setPopupData(undefined);
      }
    }
  }, [popupLocation]);

  //  SET POPUP TO CURRENT MOUSE LOCATION LOCATION
  const onHover = useCallback(
    (event: mapboxgl.MapLayerMouseEvent) => {
      const {
        lngLat: { lat, lng },
        point: { x, y },
      } = event;

      const hexCode = latLngToCell(lat, lng, RESOLUTION);
      setHoveredHex(hexCode);

      // Set cursor style based on hex state
      const isSelected = selectedHexsSet.has(hexCode);
      const isProtected = protectedHexsSet.has(hexCode);
      const isSelectable = selectableSet.has(hexCode);
      const isDeselectable = isSelected && !isProtected;

      if (isDeselectable) {
        setCursorStyle('pointer');
      } else if (isSelectable) {
        setCursorStyle('pointer');
      } else if (isProtected) {
        setCursorStyle('default');
      } else {
        setCursorStyle('default');
      }

      setPopupLocation({
        lat,
        lng,
        x,
        y,
      });
    },
    [selectedHexsSet, protectedHexsSet, selectableSet],
  );

  // Memoized GeoJSON for non-key dealers
  const nonKeyDealerGeoJson = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: dealerList
        .filter((dealer) => !keyDealers.includes(dealer.dealerId))
        .map((dealer) => ({
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: [dealer.location.lng, dealer.location.lat],
          },
          properties: {
            dealerId: dealer.dealerId,
            dealerType: dealer.dealerType,
          },
        })),
    }),
    [dealerList, keyDealers],
  );

  //* HEX LAYERS
  const baseHexLayer = useMemo(() => {
    return chunkedBaseHexArray.map((chunk, i) => {
      return returnHexLayer(returnGeoJSON(chunk), `local-market-map-${i}`);
    });
  }, [chunkedBaseHexArray]);

  const protectedHexsLayer = useMemo(() => {
    return returnHexLayer(protectedHexArray, 'protected-hexs');
  }, [protectedHexArray]);

  const selectedHexLayer = useMemo(() => {
    return returnHexLayer(selectedHexArray, 'selected-hexs');
  }, [selectedHexArray]);

  const highlightedHexLayer = useMemo(() => {
    return returnHexLayer(highlightedDealerCell, 'highlighted-hexs');
  }, [highlightedDealerCell]);

  const hoveredHexLayer = useMemo(() => {
    if (!hoveredHex || !data?.hexData[hoveredHex]) {
      return null;
    }

    const hoveredCell: HexArrayData[] = [
      {
        ...data.hexData[hoveredHex],
        hex: hoveredHex,
        borderColor: '#000',
        borderOpacity: 1,
        color: '#555',
        opacity: 0.1,
      },
    ];

    const geoJson = returnGeoJSON(hoveredCell);

    // Use red border if this is a protected or non-selectable cell that was clicked
    const borderColor =
      protectedCellClicked === hoveredHex || nonSelectableCellClicked === hoveredHex
        ? '#ff0000'
        : '#091e2e';

    return (
      <Source id='hovered-hex' key='hovered-hex' type='geojson' data={geoJson}>
        {/* Hover border */}
        <Layer
          type='line'
          paint={{
            'line-color': borderColor,
            'line-width': 1,
            'line-opacity': 1,
          }}
        />
      </Source>
    );
  }, [hoveredHex, data?.hexData, protectedCellClicked, nonSelectableCellClicked]);

  const handleCellSelection = (isIndividualDeselection = false) => {
    if (data?.hexData) {
      let inRadiusCells: string[];

      if (isIndividualDeselection) {
        // For individual deselection, only remove the specific cell
        const deselectedHexCode = deselectableDealers[0]?.hexCode;
        if (deselectedHexCode) {
          inRadiusCells = selectedHexs.filter((hex) => hex !== deselectedHexCode);
        } else {
          return; // No cell to deselect
        }
      } else {
        // For slider-based selection, recalculate all cells based on radius
        inRadiusCells = Object.keys(data?.hexData).filter(
          (hex) =>
            data?.hexData[hex].inclusionRadius <= sliderValue[0] || protectedHexsSet.has(hex),
        );
      }

      // Check if dealers have been removed
      // get hex for each dealer
      const keyDealersWithHex = dealerListWithHexs?.filter((dealer) =>
        keyDealers.includes(dealer.dealerId),
      );

      // check if there are dealers outside of selected cells
      const inRadiusSet = new Set(inRadiusCells);
      const deslectedKeyDealers = keyDealersWithHex?.filter(
        (dealer) => !inRadiusSet.has(dealer.hexCode) && !protectedHexsSet.has(dealer.hexCode),
      );

      const dealerIdArray = deslectedKeyDealers?.map((dealer) => dealer.dealerId);

      setSelectedHexs(inRadiusCells);
      clearDealers(dealerIdArray);

      if (!isIndividualDeselection) {
        setPreviousSliderValue(sliderValue);
      }

      addToast({
        status: 'info',
        duration: 5000,
        description: 'Remember to save your changes',
      });

      if (deslectedKeyDealers && deslectedKeyDealers?.length > 0) {
        const toastText = (
          <Flex column>
            Cell deselected! {deslectedKeyDealers.length} key competitor
            {deslectedKeyDealers.length > 1 ? 's' : ''} removed:
            <br />
            {deslectedKeyDealers.map((dealer) => (
              <Flex key={dealer.dealerId} alignItems='center' gap='8px' padding='0 0 0 8px'>
                <IconCircleFilled size={6} color='#fff' />
                {dealer.dealerName}
              </Flex>
            ))}
          </Flex>
        );
        addToast({
          status: 'default',
          duration: 5000,
          description: toastText,
        });
      }

      setDeselectableDealers([]);
    }
  };

  const sliderHandler = () => {
    if (data?.hexData) {
      // Get all Cells in Radius
      const inRadiusCells = Object.keys(data?.hexData).filter(
        (hex) => data?.hexData[hex].inclusionRadius <= sliderValue[0] || protectedHexsSet.has(hex),
      );
      // Check if dealers have been removed
      // get hex for each dealer
      const keyDealersWithHex = dealerListWithHexs?.filter((dealer) =>
        keyDealers.includes(dealer.dealerId),
      );

      // check if there are dealers outside of selected cells
      const inRadiusSet = new Set(inRadiusCells);
      const deslectedKeyDealers = keyDealersWithHex?.filter(
        (dealer) => !inRadiusSet.has(dealer.hexCode) && !protectedHexsSet.has(dealer.hexCode),
      );

      if (deslectedKeyDealers) {
        // once the deselectableDealers is set the deselectable dealer modal will pop up
        setDeselectableDealers(deslectedKeyDealers);
        setIsSliderModal(true);
      }

      if (deslectedKeyDealers?.length === 0) {
        handleCellSelection();
      }
    }
  };

  const handleSliderCellDeselection = () => {
    if (deselectableDealers.length > 0) {
      // Use the merged handleCellSelection for slider-based deselection
      handleCellSelection(false);
    }
  };

  const circleComponent = createGeoJSONCircleMiles(
    [data?.mapCenter.lng ?? 0, data?.mapCenter.lat ?? 0],
    sliderValue[0] ?? 0,
  );

  return (
    <div className={styles.marketmap}>
      <SelectorModal
        isOpen={!!deselectableDealers.length}
        onCancel={() => {
          setDeselectableDealers([]);
          setIsSliderModal(false);
        }}
        onContinue={isSliderModal ? handleSliderCellDeselection : () => handleCellSelection(true)}
        dealers={deselectableDealers}
        dealerList={dealerList}
      />
      <div className={styles.mapContainer} ref={ref}>
        {/* CUSTOM OVERLAY */}
        <div className={styles.mapOverlay}>
          <SummaryPanel selectedHexs={selectedHexs} data={data} />
          <div className={classnames([styles.sliderToggleButton, isSliderOpen && styles.open])}>
            <IconButton
              variants='outline'
              aria-label='toggle-radius-slider'
              size='md'
              onClick={() => {
                setIsSliderOpen(!isSliderOpen);
              }}
              showBackgroundColor
              tooltip='Bulk select cells by range'
              tooltipSide='left'
            >
              <IconPlus />
            </IconButton>
          </div>
          {isSliderOpen && (
            <SliderPopover
              sliderValue={sliderValue}
              setSliderValue={setSliderValue}
              previousSliderValue={previousSliderValue}
              sliderHandler={sliderHandler}
            />
          )}
        </div>

        {/* MAP */}

        <Map
          key={reRenderKey}
          initialViewState={{
            latitude: data?.mapCenter.lat,
            longitude: data?.mapCenter.lng,
            zoom: 7,
          }}
          mapStyle='mapbox://styles/mapbox/light-v9'
          mapboxAccessToken={import.meta.env.VITE_APP_MAPBOX_API_TOKEN}
          // This is a style hack to make the map act responsively
          style={{
            position: 'relative',
            height: 'inherit',
            width: 'inherit',
          }}
          onMouseMove={(e) => (e ? onHover(e) : null)}
          onClick={onClickMapHandler}
          onDrag={() => setPopupData(undefined)}
          cursor={cursorStyle} // Apply the dynamic cursor style
          onMouseOut={() => {
            setPopupData(undefined);
            setHoveredHex(undefined);
          }}
          onZoom={(e) => setMapZoom(e.viewState.zoom)}
          reuseMaps
          interactiveLayerIds={['protected-hexs']}
        >
          <NavigationControl position='bottom-right' />

          {/* POPOUP */}
          {popupData && (
            <div
              className={styles.popupContainer}
              style={{
                left: popupLeftPositionFilter(popupLocation?.x, mapDimensions?.width),
                top: popupBottomPositionFilter(popupLocation?.y, mapDimensions?.height),
              }}
            >
              <MapPopup popupData={popupData} />
            </div>
          )}

          {/* Quick select circle overlay */}
          {isSliderOpen && (
            <Source key={`circle`} id={`circle`} type='geojson' data={circleComponent}>
              <Layer
                id={`borders`}
                type='line'
                paint={{
                  'line-dasharray': [2, 2],
                  'line-color': '#0D2B41',
                  'line-width': 3,
                }}
              />
            </Source>
          )}

          {protectedHexsLayer}
          {baseHexLayer}
          {selectedHexLayer}
          {highlightedHexLayer}
          {hoveredHexLayer}

          {/* Non-key dealers as performant Mapbox layer */}
          <Source id='non-key-dealers' type='geojson' data={nonKeyDealerGeoJson}>
            <Layer
              id='dealer-points'
              type='circle'
              paint={{
                'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 10, 8],
                'circle-color': [
                  'match',
                  ['get', 'dealerType'],
                  'franchise',
                  '#A90DF8', // New color for franchise
                  '#B09436', // Default color for any other type
                ],
                'circle-opacity': 0.4,
                'circle-stroke-width': 1,
                'circle-stroke-color': '#fff',
              }}
            />
          </Source>

          {/* Key dealers as React Markers with StarIconWithBorder */}
          {dealerList
            .filter((dealer) => keyDealers.includes(dealer.dealerId))
            .map((dealer) => (
              <Marker
                key={dealer.dealerId}
                longitude={dealer.location.lng}
                latitude={dealer.location.lat}
                anchor='center'
                style={{ pointerEvents: 'none' }} // doesn't change cursor style for key dealers
              >
                <div
                  style={{
                    transform: `scale(${Math.max(0.8, Math.min(2, mapZoom / 8))})`,
                    transition: 'transform 0.2s ease-out',
                  }}
                >
                  <StarIconWithBorder
                    color={
                      dealer.dealerType === 'franchise'
                        ? COLOR.SPARKY_PURPLE_400
                        : COLOR.SPARKY_YELLOW_500
                    }
                  />
                </div>
              </Marker>
            ))}

          {/* Main dealer as React Marker with CircleIconWithBorder */}
          {data?.mapCenter && (
            <Marker
              key='primary dealer'
              longitude={data.mapCenter.lng}
              latitude={data.mapCenter.lat}
              anchor='center'
            >
              <CircleIconWithBorder color={'#000'} />
            </Marker>
          )}
        </Map>
      </div>
    </div>
  );
}

//* Convert Data to GeoJSON
function returnGeoJSON(data: HexArrayData[]) {
  const geoData = data.map((hex) => {
    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [cellToBoundary(hex.hex, true)],
      },
      properties: { ...hex },
    };
  });

  return {
    type: 'FeatureCollection',
    features: geoData,
  } as FeatureCollection<Geometry, GeoJsonProperties>;
}

//* Perform both addSelectedProperty and addOpacityProperty
function addHexProperties(data: Hex | undefined) {
  const workingData: Hex = { ...data };
  addOpacityProperty(workingData);
  return workingData;
}

//* Add opacity property to hexData
function addOpacityProperty(data: Hex | undefined) {
  let dataHexs: Hex = { ...data };
  const hexKeys = Object.keys(dataHexs);

  let totalShoppers = 0;
  let nonZeroCount = 0;
  for (const key of hexKeys) {
    const shoppers = dataHexs[key].inMarketShoppers;
    if (shoppers > 0) {
      totalShoppers += shoppers;
      nonZeroCount++;
    }
  }

  const averageShoppers = nonZeroCount > 0 ? totalShoppers / nonZeroCount : 0;

  for (const hex of hexKeys) {
    const shoppers = dataHexs[hex].inMarketShoppers;

    switch (true) {
      case shoppers > averageShoppers * 10:
        dataHexs[hex].opacity = 0.8;
        break;
      case shoppers > averageShoppers * 7:
        dataHexs[hex].opacity = 0.7;
        break;
      case shoppers > averageShoppers * 5:
        dataHexs[hex].opacity = 0.6;
        break;
      case shoppers > averageShoppers * 3:
        dataHexs[hex].opacity = 0.5;
        break;
      case shoppers > averageShoppers * 2:
        dataHexs[hex].opacity = 0.4;
        break;
      case shoppers > averageShoppers:
        dataHexs[hex].opacity = 0.3;
        break;
      case shoppers < averageShoppers / 5:
        dataHexs[hex].opacity = 0.1;
        break;
      case shoppers < averageShoppers / 3:
        dataHexs[hex].opacity = 0.15;
        break;
      case shoppers < averageShoppers / 2:
        dataHexs[hex].opacity = 0.2;
        break;
      case shoppers < averageShoppers:
        dataHexs[hex].opacity = 0.25;
        break;
      default:
        dataHexs[hex].opacity = 0.1;
        break;
    }
  }
}

//* build list of selectable Hexs
function buildSelectableList(selectedHexsSet: Set<string>, dataHexs: Hex) {
  const selectableSet = new Set<string>();
  for (const hex of selectedHexsSet) {
    for (const cell of gridDisk(hex, 1)) {
      if (cell in dataHexs && !selectedHexsSet.has(cell)) {
        selectableSet.add(cell);
      }
    }
  }
  return selectableSet;
}

function returnHexLayer(data: FeatureCollection<Geometry, GeoJsonProperties>, slug: string) {
  return (
    <Source id={slug} key={slug} type='geojson' data={data}>
      {/* HEX FILL */}
      <Layer
        id={slug}
        type='fill'
        paint={{
          'fill-color':
            slug === 'highlighted-hexs'
              ? '#f00'
              : {
                  type: 'identity',
                  property: 'color',
                },
          'fill-opacity':
            slug === 'highlighted-hexs'
              ? 0.4
              : {
                  type: 'identity',
                  property: 'opacity',
                },
        }}
      />

      {/* HEX OUTLINE - only for highlighted hexes */}
      {slug === 'highlighted-hexs' && (
        <Layer
          {...{
            type: 'line',
            paint: {
              'line-color': {
                type: 'identity',
                property: 'borderColor',
              },
              'line-width': 2,
              'line-opacity': {
                type: 'identity',
                property: 'borderOpacity',
              },
            },
          }}
        />
      )}
    </Source>
  );
}
